// components
import { ControlledTextField } from 'components/shared/ControlledFields';
import {
  GridCompact12,
  GridCompact4,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import { ControlledCheckboxCompact } from './FlexMatchDrawerFilters.components';

// constants
import { FILTER_POPOVER_WIDTH } from './FilterPopper.consts';

// hooks
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// types
import { FlexMatchFilterFields } from './FlexMatchFilters.types';

// utils
import { AnimatePresence } from 'framer-motion';
import { StyledTypographyBodyBold } from 'components/shared/Typography/Typography.styles';

// styles
import { StyledAddRowMotionBoxNoPaddingBase } from './FlexMatchFilters.styles';
import { StyledBoxFlexCenter } from 'components/shared/Box/Box.styles';
import { StyledTextFieldWithMarginTop } from './FlexMatchDrawerFilters.styles';

export const FlexMatchDrawerFilters = ({
  showMoreFilters = false,
  showSettings = false
}: {
  showMoreFilters?: boolean;
  showSettings?: boolean;
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const { t: tCommon } = useTranslation('common');
  const { control } = useFormContext<FlexMatchFilterFields>();

  return (
    <GridContainer spacing={3} maxWidth={FILTER_POPOVER_WIDTH}>
      {showSettings && (
        <GridCompact12>
          <StyledBoxFlexCenter>
            <ControlledTextField
              control={control}
              name="stepName"
              label={t('stepName')}
            />
          </StyledBoxFlexCenter>
        </GridCompact12>
      )}
      <GridCompact4>
        <StyledTypographyBodyBold variant="body1" component="p">
          {t('kind')}
        </StyledTypographyBodyBold>
        <ControlledCheckboxCompact
          name="employeeFlex"
          control={control}
          label={t('employeeFlex')}
          disabled
        />
        <ControlledCheckboxCompact
          name="employeeUsedAsFlex"
          control={control}
          label={t('employeeUsedAsFlex')}
        />
      </GridCompact4>
      <GridCompact4>
        <StyledTypographyBodyBold variant="body1" component="p">
          {tCommon('cluster')}
        </StyledTypographyBodyBold>
        <ControlledCheckboxCompact
          name="clusterPreference1"
          control={control}
          label={tCommon('preference1')}
        />
        <ControlledCheckboxCompact
          name="clusterPreference2"
          control={control}
          label={tCommon('preference2')}
        />
      </GridCompact4>
      <GridCompact4>
        <StyledTypographyBodyBold variant="body1" component="p">
          {tCommon('planUnit')}
        </StyledTypographyBodyBold>
        <ControlledCheckboxCompact
          name="costCenterPreference1"
          control={control}
          label={tCommon('preference1')}
        />
        <ControlledCheckboxCompact
          name="costCenterPreference2"
          control={control}
          label={tCommon('preference2')}
        />
      </GridCompact4>
      <AnimatePresence initial={false}>
        {showMoreFilters && (
          <>
            <StyledAddRowMotionBoxNoPaddingBase key="motion-show-more-flex-drawer-filters">
              <GridCompact4>
                <StyledTypographyBodyBold variant="body1" component="p">
                  {tCommon('function')}
                </StyledTypographyBodyBold>
                <ControlledCheckboxCompact
                  name="functionPreference1"
                  control={control}
                  label={tCommon('preference1')}
                />
                <ControlledCheckboxCompact
                  name="functionPreference2"
                  control={control}
                  label={tCommon('preference2')}
                />
              </GridCompact4>

              <GridCompact4>
                <StyledTypographyBodyBold variant="body1" component="p">
                  {tCommon('availability')}
                </StyledTypographyBodyBold>
                <ControlledCheckboxCompact
                  name="availabilityOnSameDay"
                  control={control}
                  label={t('duringDuty')}
                />
                <ControlledCheckboxCompact
                  name="availabilityOverlap"
                  control={control}
                  label={t('outOfDuty')}
                />
              </GridCompact4>

              <GridCompact4>
                <StyledTypographyBodyBold variant="body1" component="p">
                  {t('cumulativeBalance')}
                </StyledTypographyBodyBold>
                <StyledTextFieldWithMarginTop
                  control={control}
                  name="balanceHours"
                  label={t('cumulativeBalanceLowerThan')}
                />
              </GridCompact4>
            </StyledAddRowMotionBoxNoPaddingBase>
            <StyledAddRowMotionBoxNoPaddingBase>
              <GridCompact12>
                <StyledTypographyBodyBold variant="body1" component="p">
                  {tCommon('employee')}
                </StyledTypographyBodyBold>
                <ControlledCheckboxCompact
                  name="dutyOverlap"
                  control={control}
                  label={t('withOverlap')}
                />
                <ControlledCheckboxCompact
                  name="pool"
                  control={control}
                  label={t('inPool')}
                />
                <ControlledCheckboxCompact
                  name="qualified"
                  control={control}
                  label={t('qualified')}
                />
              </GridCompact12>
            </StyledAddRowMotionBoxNoPaddingBase>
          </>
        )}
      </AnimatePresence>
    </GridContainer>
  );
};
